import { useEffect, useRef, useState } from 'react'
import {
  getCategoryLabel,
  getCategoryMeta,
  sortCategories,
  START_HERE_SLUGS,
} from '@/lib/categories'
import { useHelpIndex } from '@/lib/data'
import { CaretRightIcon } from '@phosphor-icons/react'
import { Link, NavLink } from 'react-router-dom'

import { groupHelpCategories } from '@scrambl/shared/help/help-sections'

export function Sidebar({
  activeCategorySlug,
  activeArticleId,
  onNavigate,
}: {
  activeCategorySlug?: string | null
  activeArticleId?: string | null
  onNavigate?: () => void
}) {
  const { index } = useHelpIndex()
  const navRef = useRef<HTMLElement>(null)
  const [openSlugs, setOpenSlugs] = useState<string[]>(() =>
    activeCategorySlug ? [activeCategorySlug] : [],
  )

  useEffect(() => {
    if (activeCategorySlug && !openSlugs.includes(activeCategorySlug)) {
      setOpenSlugs(previous => [...previous, activeCategorySlug])
    }
    // Only react to the active category changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategorySlug])

  // Bring the page you're on into view inside the sidebar's own scroller (not
  // the window): an article in a later area was marked current 1,900px down a
  // list that opened at the top.
  useEffect(() => {
    const nav = navRef.current
    if (!nav || !index) return
    const target =
      nav.querySelector<HTMLElement>('[aria-current="page"]') ??
      nav.querySelector<HTMLElement>('.sidebar-group.is-active')
    if (!target) return
    let scroller: HTMLElement | null = nav
    while (
      scroller &&
      !(
        scroller.scrollHeight > scroller.clientHeight &&
        /auto|scroll/.test(getComputedStyle(scroller).overflowY)
      )
    ) {
      scroller = scroller.parentElement
    }
    if (!scroller || scroller === document.scrollingElement) return
    const view = scroller.getBoundingClientRect()
    const item = target.getBoundingClientRect()
    if (item.top < view.top || item.bottom > view.bottom) {
      scroller.scrollTop += item.top - view.top - view.height / 3
    }
  }, [index, activeCategorySlug, activeArticleId])

  if (!index) {
    return (
      <nav className="sidebar" aria-label="Help categories">
        <div className="sidebar-skeleton">
          {Array.from({ length: 9 }).map((_, position) => (
            <span
              key={position}
              style={{ width: `${55 + (position % 4) * 12}%` }}
            />
          ))}
        </div>
      </nav>
    )
  }

  const categories = sortCategories(index.categories)
  const startHere = categories.filter(row =>
    START_HERE_SLUGS.includes(row.slug),
  )
  const areaGroups = groupHelpCategories(
    categories.filter(row => !START_HERE_SLUGS.includes(row.slug)),
  )

  function toggle(slug: string) {
    setOpenSlugs(previous =>
      previous.includes(slug)
        ? previous.filter(item => item !== slug)
        : [...previous, slug],
    )
  }

  function renderGroup(slug: string) {
    const meta = getCategoryMeta(slug)
    const Icon = meta.icon
    const isOpen = openSlugs.includes(slug)
    const isActive = activeCategorySlug === slug
    const articles = index!.articles
      .filter(article => article.moduleSlug === slug)
      .sort((a, b) => a.title.localeCompare(b.title))

    return (
      <li key={slug} className={`sidebar-group${isActive ? ' is-active' : ''}`}>
        <div className="sidebar-group-row">
          <button
            type="button"
            className="sidebar-caret"
            onClick={() => toggle(slug)}
            aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${getCategoryLabel(slug)}`}
            aria-expanded={isOpen}
          >
            <CaretRightIcon
              size={11}
              weight="bold"
              className={isOpen ? 'is-open' : undefined}
            />
          </button>
          <NavLink
            to={`/categories/${encodeURIComponent(slug)}`}
            className={`sidebar-category hue-${meta.hue}`}
            onClick={() => {
              if (!isOpen) toggle(slug)
              onNavigate?.()
            }}
          >
            <Icon
              size={15}
              weight="duotone"
              className="sidebar-category-icon"
            />
            <span>{getCategoryLabel(slug)}</span>
          </NavLink>
        </div>
        {isOpen ? (
          <ul className="sidebar-articles">
            {articles.map(article => (
              <li key={article.id}>
                <Link
                  to={`/articles/${encodeURIComponent(article.id)}`}
                  className={
                    activeArticleId === article.id ? 'is-active' : undefined
                  }
                  aria-current={
                    activeArticleId === article.id ? 'page' : undefined
                  }
                  onClick={onNavigate}
                >
                  {article.title}
                </Link>
              </li>
            ))}
          </ul>
        ) : null}
      </li>
    )
  }

  return (
    <nav ref={navRef} className="sidebar" aria-label="Help categories">
      <NavLink to="/" end className="sidebar-home" onClick={onNavigate}>
        Help Center home
      </NavLink>
      <div className="sidebar-label">Start here</div>
      <ul className="sidebar-list">
        {startHere.map(row => renderGroup(row.slug))}
      </ul>
      {areaGroups.map(({ section, rows }) => (
        <div key={section.key}>
          <div className="sidebar-label">{section.label}</div>
          <ul className="sidebar-list">
            {rows.map(row => renderGroup(row.slug))}
          </ul>
        </div>
      ))}
    </nav>
  )
}
